import Phaser from 'phaser';
import type { MorseSymbol } from '@/utils/morse';
import { THEME } from '@/config/theme';
import { MORSE_SPACING, type PixelHitZones } from '@/utils/timing';

export type NoteState = 'approaching' | 'in_zone' | 'hit' | 'missed' | 'passed';

export interface MorseNoteConfig {
  symbol: MorseSymbol;
  charIndex: number;
  symbolIndex: number;
  isLastInChar: boolean;
  expectedChar: string;
  spawnX: number;        // Initial X position
  speed: number;         // Pixels per second
  hitZoneX: number;      // X position of hit zone
  trackY: number;        // Y position of track
  hitZones: PixelHitZones; // Pixel distances for hit quality
}

export class MorseNoteSprite extends Phaser.GameObjects.Container {
  public symbol: MorseSymbol;
  public charIndex: number;
  public symbolIndex: number;
  public isLastInChar: boolean;
  public expectedChar: string;
  public state: NoteState = 'approaching';

  private speed: number;
  private hitZoneX: number;
  private hitZones: PixelHitZones;
  private sprite: Phaser.GameObjects.Image | Phaser.GameObjects.Rectangle;
  private glowEffect: Phaser.GameObjects.Rectangle | null = null;

  constructor(scene: Phaser.Scene, config: MorseNoteConfig) {
    super(scene, config.spawnX, config.trackY);

    this.symbol = config.symbol;
    this.charIndex = config.charIndex;
    this.symbolIndex = config.symbolIndex;
    this.isLastInChar = config.isLastInChar;
    this.expectedChar = config.expectedChar;
    this.speed = config.speed;
    this.hitZoneX = config.hitZoneX;
    this.hitZones = config.hitZones;

    // Create the visual representation
    this.sprite = this.createSprite(scene);
    this.add(this.sprite);

    // Add to scene
    scene.add.existing(this);
  }

  private createSprite(scene: Phaser.Scene): Phaser.GameObjects.Image | Phaser.GameObjects.Rectangle {
    const textureKey = this.symbol === '.' ? 'dot-sprite' : 'dash-sprite';

    if (scene.textures.exists(textureKey)) {
      const img = scene.add.image(0, 0, textureKey);
      img.setOrigin(0, 0.5); // Left edge aligned, vertically centered
      return img;
    }

    // Fallback to generated shapes
    if (this.symbol === '.') {
      const dot = scene.add.rectangle(0, 0, MORSE_SPACING.dotWidth, MORSE_SPACING.noteHeight, THEME.colors.primaryHex);
      dot.setOrigin(0, 0.5);
      dot.setStrokeStyle(2, THEME.colors.primaryDarkHex);
      return dot;
    } else {
      const dash = scene.add.rectangle(0, 0, MORSE_SPACING.dashWidth, MORSE_SPACING.noteHeight, THEME.colors.primaryHex);
      dash.setOrigin(0, 0.5);
      dash.setStrokeStyle(2, THEME.colors.primaryDarkHex);
      return dash;
    }
  }

  update(_time: number, delta: number): void {
    if (this.state === 'hit' || this.state === 'passed') {
      return;
    }

    // Simple linear motion - move left
    this.x -= this.speed * (delta / 1000);

    // Update state based on position
    this.updateState();

    // Update visual based on state
    this.updateVisual();
  }

  private updateState(): void {
    const distance = this.x - this.hitZoneX;

    if (distance > this.hitZones.okZone) {
      this.state = 'approaching';
    } else if (distance >= -this.hitZones.okZone) {
      this.state = 'in_zone';
    } else if (this.state !== 'hit') {
      this.state = 'missed';
    }
  }

  private updateVisual(): void {
    if (this.state === 'in_zone' && !this.glowEffect) {
      // Add glow effect when in hit zone
      const width = this.symbol === '.' ? MORSE_SPACING.dotWidth : MORSE_SPACING.dashWidth;
      this.glowEffect = this.scene.add.rectangle(0, 0, width, MORSE_SPACING.noteHeight, THEME.colors.primaryHex, 0.3);
      this.glowEffect.setOrigin(0, 0.5);
      this.addAt(this.glowEffect, 0);
    }

    // Fade as note approaches and passes through hit zone center
    if (this.state === 'in_zone' || this.state === 'missed') {
      const distance = this.x - this.hitZoneX;
      // Fade from full opacity at okZone to 0 past the zone
      const fadeRange = this.hitZones.okZone * 1.5;
      const alpha = Math.max(0, Math.min(1, (distance + this.hitZones.okZone) / fadeRange));
      this.setAlpha(alpha);
    }

    if (this.state === 'missed' && this.sprite instanceof Phaser.GameObjects.Rectangle) {
      this.sprite.setFillStyle(THEME.colors.borderHex);
      this.sprite.setStrokeStyle(2, THEME.colors.backgroundPanelHex);
    }
  }

  /**
   * Get the pixel offset from hit zone (negative = past, positive = approaching)
   */
  getPositionOffset(): number {
    return this.x - this.hitZoneX;
  }

  /**
   * Check if note should be marked missed (past the ok zone)
   */
  shouldMarkMissed(): boolean {
    if (this.state === 'hit' || this.state === 'passed') return false;
    return (this.x - this.hitZoneX) < -this.hitZones.okZone;
  }

  /**
   * Mark this note as hit
   */
  markHit(): void {
    this.state = 'hit';

    // Visual feedback
    if (this.sprite instanceof Phaser.GameObjects.Rectangle) {
      this.sprite.setFillStyle(THEME.colors.cyanHex);
    }

    // Fade out
    this.scene.tweens.add({
      targets: this,
      alpha: 0,
      y: this.y - 30,
      duration: 200,
      onComplete: () => this.destroy(),
    });
  }

  /**
   * Mark this note as passed (missed and off-screen)
   */
  markPassed(): void {
    this.state = 'passed';
    this.destroy();
  }

  /**
   * Check if note has passed the hit zone and should be cleaned up
   */
  hasPassed(): boolean {
    if (this.state === 'passed') return true;
    // Note has passed when it's missed and past left track boundary
    if (this.state === 'missed' && this.x < 40) return true;
    return false;
  }
}
